/**
 * ISMOOLI Recently Viewed
 * Client-side product view history for discovery surfaces.
 */
class GlycRecentlyViewed {
  static STORAGE_KEY = 'glyc-recently-viewed';
  static CARD_SECTION_ID = 'glyc-product-card-render';

  static getLimit() {
    return parseInt(document.documentElement.dataset.glycRecentlyViewedLimit, 10) || 8;
  }

  static getProducts() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  static saveProducts(products) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(products));
    } catch {
      /* storage unavailable */
    }
  }

  static track(product) {
    if (!product || !product.handle) return;

    const products = this.getProducts().filter((item) => item.handle !== product.handle);
    products.unshift(product);

    if (products.length > this.getLimit()) {
      products.length = this.getLimit();
    }

    this.saveProducts(products);
    document.dispatchEvent(new CustomEvent('glyc:recently-viewed-updated'));
  }

  static filterProducts(excludeHandle) {
    const products = this.getProducts();
    if (!excludeHandle) return products.slice(0, this.getLimit());
    return products.filter((item) => item.handle !== excludeHandle).slice(0, this.getLimit());
  }

  static escape(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  static async fetchCommerceCard(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${this.CARD_SECTION_ID}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return null;

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      return doc.querySelector('[data-glyc-product-card-render]');
    } catch {
      return null;
    }
  }

  static async renderList(listElement, emptyElement, rootElement, products, mode = 'card') {
    if (!listElement) return;

    listElement.innerHTML = '';

    if (products.length === 0) {
      if (emptyElement) emptyElement.hidden = false;
      if (rootElement) rootElement.hidden = !Shopify.designMode;
      return;
    }

    if (emptyElement) emptyElement.hidden = true;
    if (rootElement) rootElement.hidden = false;

    if (mode === 'card') {
      const cards = await Promise.all(products.map((product) => this.fetchCommerceCard(product.handle)));
      const validCards = cards.filter(Boolean);

      if (validCards.length === 0) {
        if (emptyElement) emptyElement.hidden = false;
        if (rootElement) rootElement.hidden = !Shopify.designMode;
        return;
      }

      validCards.forEach((card) => listElement.appendChild(card));
      return;
    }

    products.forEach((product, index) => {
      listElement.appendChild(this.createCompactItem(product, index));
    });
  }

  static createCompactItem(product, index) {
    const item = document.createElement('li');
    item.className = 'glyc-predictive-search__recent-item';

    const link = document.createElement('a');
    link.href = product.url || `/products/${product.handle}`;
    link.className = 'glyc-predictive-search__recent-link glyc-focus-ring';
    link.setAttribute('tabindex', '-1');
    link.innerHTML = `
      ${
        product.image
          ? `<img class="glyc-predictive-search__recent-image" src="${product.image}" alt="" width="40" height="40" loading="lazy">`
          : '<span class="glyc-predictive-search__recent-image glyc-predictive-search__recent-image--placeholder" aria-hidden="true"></span>'
      }
      <span class="glyc-predictive-search__recent-text">
        <span class="glyc-predictive-search__recent-title">${this.escape(product.title)}</span>
        ${product.subtitle ? `<span class="glyc-predictive-search__recent-subtitle">${this.escape(product.subtitle)}</span>` : ''}
      </span>
    `;
    link.setAttribute('id', `predictive-search-option-recent-${index + 1}`);
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');
    item.setAttribute('id', `predictive-search-option-recent-${index + 1}`);

    item.appendChild(link);
    return item;
  }

  static bindRoot(root) {
    if (!root || root.dataset.glycRecentlyViewedBound === 'true') return;

    const list = root.querySelector('[data-glyc-recently-viewed-grid]');
    const empty = root.querySelector('[data-glyc-recently-viewed-section-empty]');
    const skeleton = root.querySelector('.glyc-recently-viewed__skeleton');
    const excludeHandle = root.dataset.excludeHandle || '';
    const mode = root.dataset.layout === 'compact' ? 'compact' : 'card';

    const render = async () => {
      if (skeleton) skeleton.hidden = true;
      if (list) list.hidden = false;
      const products = this.filterProducts(excludeHandle);
      await this.renderList(list, empty, root, products, mode);
    };

    render();
    root.dataset.glycRecentlyViewedBound = 'true';
    document.addEventListener('glyc:recently-viewed-updated', render);
  }

  static bindPredictive(idleRoot) {
    if (!idleRoot) return;

    const list = idleRoot.querySelector('[data-glyc-recently-viewed-list]');
    const empty = idleRoot.querySelector('[data-glyc-recently-viewed-empty]');

    const render = async () => {
      const products = this.filterProducts('');
      await this.renderList(list, empty, idleRoot, products, 'compact');
    };

    render();
    document.addEventListener('glyc:recently-viewed-updated', render);
  }

  static initAll() {
    document.querySelectorAll('[data-glyc-recently-viewed-root]').forEach((root) => {
      this.bindRoot(root);
    });
  }
}

window.GlycRecentlyViewed = GlycRecentlyViewed;

document.addEventListener('DOMContentLoaded', () => {
  GlycRecentlyViewed.initAll();
});
